import { type PRIVATE_PRODUCT_ERROR_CODES, type CountryCode, type ProjectCode } from '@constants'
import { type ValueOf } from '@dni-types/utilities/value-of'
import { type Nullable } from '../nullable'
import { type Campaign } from './campaigns'
import { type FastpassOrderItem } from './orders'
import { type PaymentMethod } from './payment'
import { type ECommerceProduct, type WpProduct, type WpProductStatus } from './products'
import { type PromotionGroup } from './promotion-groups'
import { type DeliveryClass, type ShippingMethod } from './shipping'
import { type SubshipInfo } from './subship'
import { type TicketIssuance } from './ticket'

export const TICKET_TYPE = {
  /** 未領取 */
  UNCLAIMED: 'unclaimed',
  /** 已領取 */
  CLAIMED: 'claimed',
  /** 已使用 */
  USED: 'used',
  /** 已過期 */
  EXPIRED: 'expired',
  /** 資格不符 */
  REVOKED: 'revoked',
} as const

export const PROMOTION_TYPE = {
  /** 優惠活動 */
  COUPON: 'coupon',
  /** 優惠券 */
  TICKET: 'ticket',
}

export enum TargetAudiencesFieldName {
  ROLE_IDS = 'role_ids',
  WP_USER_IDS = 'wp_user_ids',
  MERCHANT_BILLING_FIRST_NAMES = 'merchant_billing_first_names',
  MERCHANT_CATEGORY_NAMES = 'merchant_category_names',
  EMAIL_DOMAIN = 'email_domain',
}

export enum PRODUCT_DELIVERY_CLASSES {
  NORMAL = 'normal',
  FROZEN = 'frozen',
  DONATION = 'donation',
  SUBSHIP = 'subship',
}

export enum CouponPlatform {
  FASTPASS = 'fastpass',
  WORDPRESS = 'wordpress',
}

export enum ApplicableProductScope {
  ALL_PRODUCT = 'all_product',
  SPECIFIC_PRODUCT = 'specific_product',
  WAREHOUSE_PRODUCT = 'warehouse_product',
}
/**
 * 表示優惠作用範圍，n可代表數量或是金額，over_n = n以上, per_n = 每n, n_m = 每n中的m, all = 所有商品
 */
export enum DiscountApplicationMethod {
  OVER_N = 'over_n',
  PER_N = 'per_n',
  N_M = 'n_m',
  ALL = 'all',
}

export enum DiscountTriggerMethod {
  QUANTITY = 'quantity',
  AMOUNT = 'amount',
  UNLIMITED = 'unlimited',
}

export enum PromotionType {
  /** 折抵 % */
  DISCOUNT_RATE = 'discount_rate',
  /** 折抵(元) */
  DISCOUNT_PRICE = 'discount_price',
  /** 送贈品 */
  GIVEAWAY = 'giveaway',
  /** 固定金額 */
  FIXED_PRICE = 'fixed_price',
  /** 運費優惠 */
  DIS_SHIPPING = 'dis_shipping',
  /** 免運 */
  FREE_SHIPPING = 'free_shipping',
}

export enum UsageMethod {
  AUTO = 'auto',
  MANUAL = 'manual',
  RECEIVE = 'receive',
  CLAIM = 'claim',
}

export enum NewOrExistingCustomerRestriction {
  OLD = 'old',
  NEW = 'new',
  UNLIMITED = 'unlimited',
}

export const newOrExistingCustomerRestrictionMap = {
  [NewOrExistingCustomerRestriction.OLD]: '已成立過訂單的客人（舊客）',
  [NewOrExistingCustomerRestriction.NEW]: '未成立過訂單的客人（新客）',
  [NewOrExistingCustomerRestriction.UNLIMITED]: '無限制',
}

export interface PromotionTypeInfo {
  promotion_type: PromotionType
  display_name: string
  discount_application_methods: {
    discount_application_method: string
  }[]
}
export interface CouponComposition {
  discount_trigger_method: DiscountTriggerMethod
  display_name: string
  promotion_types: PromotionTypeInfo[]
}
export enum CouponType {
  /**
   * 全館商品優惠券
   */
  ALL_PRODUCT = 'all_product',
  /**
   * 指定商品優惠券
   */
  SPECIFIC_PRODUCT = 'specific_product',
  /**
   * 寄倉商品優惠券
   */
  WAREHOUSE_PRODUCT = 'warehouse_product',
  /**
   * 折運費
   */
  DIS_SHIPPING = 'dis_shipping',
}

export enum DiscountItemType {
  COUPON = 'coupon',
  DISCOUNT = 'discount',
  REWARD_POINT = 'reward_point',
  REFERRAL_CODE = 'referral_code',
  SHIPPING_COUPON = 'shipping_coupon',
  ADDON = 'addon',
  ADDON_V2 = 'addon_v2',
}

export enum ProductTriggerDeliveryClasses {
  NORMAL = 'normal',
  FROZEN = 'frozen',
  SUB_SHIP = 'subship',
  DONATION = 'donation',
}

export enum AppliedStatus {
  APPLIED = 'applied',
  UNABLE = 'unable',
  NOT_REACHED = 'not_reached',
  INVISIBLE = 'invisible',
}

export interface CouponSettingOptions {
  brand_info: {
    country_code: CountryCode
    display_name: string
    available_project_codes: {
      project_code: ProjectCode
      display_name: string
    }[]
  }[]

  coupon_composition: {
    shipping_coupon: CouponComposition[]
    total_coupon: CouponComposition[]
  }

  coupon_combination: {
    shipping_coupon: {
      display_name: string
      composition: CouponComposition[]
    }[]
    total_coupon: {
      display_name: string
      composition: CouponComposition[]
    }[]
  }
  wp_sites: {
    id: number
    country_code: CountryCode
    platform_name: string
    project_code: ProjectCode
  }[]
}

export interface CouponsSearchAvailableValue {
  key: CountryCode
  display_name: string
}

export interface CouponCriterion extends CouponCriterionBase {
  id: number
  coupon_id: Coupon['id']
  created_at: string
  updated_at: string
}

export interface CouponCriterionBase {
  criterion_quantity: Nullable<number>
  criterion_amount: Nullable<number>
  discount_rate: Nullable<number>
  discount_amount: Nullable<number>
  fixed_price: Nullable<number>
  dis_shipping_amount: Nullable<number>
  is_give_all: boolean
  gift_selection_count: Nullable<number>
  display_name: string
  m_quantity: Nullable<number>
  giveaways: Nullable<AppliedProduct[]>
}
export type ActivityTimeSetting = {
  platform: CouponPlatform.FASTPASS
  identifier: Campaign['campaign_id']
  platform_name: string
  enable: boolean
  start_time: Nullable<string>
  end_time: Nullable<string>
  link?: Nullable<string>
  isCustomDate: boolean
} | {
  platform: CouponPlatform.WORDPRESS
  identifier: number
  platform_name: string
  enable: boolean
  start_time: Nullable<string>
  end_time: Nullable<string>
  link?: Nullable<string>
  promotion_group_id?: Nullable<PromotionGroup['id']>
  /** 領取開始時間 */
  claim_start_at?: Nullable<string>
  /** 領取結束時間 */
  claim_end_at?: Nullable<string>
  /** 總領取數量 */
  claim_total_limit?: Nullable<number>
  /** 每人領取數量 */
  claim_limit_per_user?: Nullable<number>
  /** 已領取數量 */
  claimed_total?: Nullable<number>
  /** 已使用數量 */
  claim_used_total?: Nullable<number>
  isCustomDate: boolean
  isClaimDateCustom?: Nullable<boolean>
}

export interface CouponAppliedProducts {
  coupon_id: Coupon['id']
  coupon_code: Coupon['coupon_code']
  display_name: Coupon['display_name']
  description: Coupon['description']
  applied_product_categories: Nullable<string[]>
  applied_products: AppliedProduct[]
}

export interface CouponProduct {
  product_id: WpProduct['product_id']
  status: WpProductStatus
  product_title: WpProduct['product_title']
  stock: WpProduct['stock']
  is_manage_stock: WpProduct['is_manage_stock']
  thumbnail: WpProduct['thumbnail']
  variation_products: WpProduct['variation_products']
  sku: WpProduct['sku']
  product_frame_id: Nullable<number>
  product_frame_name: Nullable<string>
  is_simple_product?: boolean
  delivery_class?: DeliveryClass
}

export interface CouponSpecificProducts {
  coupon_id: Coupon['id']
  product_groups: {
    product_id: number
    project_code: ProjectCode
  }[][]
}

export interface AppliedProduct {
  project_code: ProjectCode
  products: CouponProduct[]
}

export interface Coupon {
  id: number
  created_at: string
  updated_at: string
  display_name: string
  description: Nullable<string>
  activity_time_settings: Nullable<ActivityTimeSetting[]>
  project_codes: ProjectCode[]
  country_code: CountryCode
  thumbnail_image_id: Nullable<number>
  thumbnail_image_url: Nullable<string>
  pc_banner_image_id: Nullable<number>
  pc_banner_image_url: Nullable<string>
  mobile_banner_image_id: Nullable<number>
  mobile_banner_image_url: Nullable<string>
  coupon_code: string
  usage_method: UsageMethod
  is_claimable: boolean
  special_link?: Nullable<string>
  giveaways?: Nullable<AppliedProduct[]>
  max_discount_amount: Nullable<number>
  applicable_targets: Nullable<CouponAudiences[]>
  new_or_existing_customer_restriction: NewOrExistingCustomerRestriction
  lifetime_usage_limit_per_user: Nullable<number>
  display_notification_when_applied: boolean
  is_regular_giveaway: boolean
  is_progress_visible: boolean
  max_giveaway_repeatable_count: Nullable<number>
  allowed_usage_count: Nullable<number>
  last_update_user_info: {
    id: number
    name: string
    update_time: string
  }
  updated_user_id: number
  used_coupons_count: number
  last_sent_at: Nullable<string>
  excluded_products: Nullable<AppliedProduct[]>
  regular_giveaway_prefix: Nullable<string>
  used_count: number
  applied_order_total: CampaignCoupon['applied_order_total']
  /** 訂單數（有使用此優惠折扣的去重訂單數） */
  order_count?: Nullable<number>
  /** 平均客單價（訂單總金額 / 訂單數，無訂單回 0） */
  average_order_value?: Nullable<number>
  criterion_groups: CouponCriterionGroup[]
  criterion_group_count: number
  promotion_groups: {
    id: Nullable<number>
    name: Nullable<string>
  }[]
  is_addon_v2: boolean
  addon_products: {
    addon_price: number
    criterion_amount: number
    is_active: boolean
    parent_product_name: string
    purchase_limit: Nullable<number>
    variation_specifications: string
  }[]
}

export interface CampaignCoupon {
  coupon_id: number
  enable: boolean
  start_time: Nullable<string>
  end_time: Nullable<string>
  display_name: string
  usage_method: string
  coupon_code: Nullable<string>
  allowed_usage_count: Nullable<number>
  used_count: Nullable<number>
  applied_order_total: Nullable<number>
}

export interface CouponAudiences {
  project_code: ProjectCode
  wp_user_ids: Nullable<string[]>
  exclude_wp_user_ids: Nullable<string[]>
  merchant_billing_first_names: Nullable<string[]>
  merchant_category_names: Nullable<string[]>
  exclude_merchant_billing_first_names: Nullable<string[]>
  exclude_merchant_category_names: Nullable<string[]>
  role_ids: Nullable<string[]>
  email_domains: Nullable<string[]>
}

export type CouponList = {
  id: Coupon['id']
  display_name: Coupon['display_name']
  description: Coupon['description']
  activity_time_settings: Coupon['activity_time_settings']
  project_codes: Coupon['project_codes']
  country_code: Coupon['country_code']
  applicable_product_scope: Coupon['criterion_groups'][number]['applicable_product_scope']
  discount_trigger_method: Coupon['criterion_groups'][number]['discount_trigger_method']
  coupon_code: Coupon['coupon_code']
  usage_method: Coupon['usage_method']
  promotion_type: Coupon['criterion_groups'][number]['promotion_type']
  applicable_targets: CouponAudiences[]
  discount_application_method: Coupon['criterion_groups'][number]['discount_application_method']
}[]

export interface CouponTag {
  id: number
  color: string
  nams: string
  description: string
  created_at: string
  updated_at: string
}

export interface CouponCriterionGiveawayItem {
  id: number
  coupon_criterion_id: number
  project_code: ProjectCode
  parent_product_id: number
  product_id: number
  sku: string
  created_at: string
  updated_at: string
  thumbnail: Nullable<string>
  title: string
  parent_title: string
  attributes: Partial<Record<string, string>>
}

export interface AvailableCouponCriterion {
  id: CouponCriterion['id']
  coupon_id: CouponCriterion['coupon_id']
  criterion_quantity: CouponCriterion['criterion_quantity']
  m_quantity: CouponCriterion['m_quantity']
  criterion_amount: CouponCriterion['criterion_amount']
  discount_rate: CouponCriterion['discount_rate']
  discount_amount: CouponCriterion['discount_amount']
  dis_shipping_amount: CouponCriterion['dis_shipping_amount']
  fixed_price: CouponCriterion['fixed_price']
  is_give_all: CouponCriterion['is_give_all']
  gift_selection_count: CouponCriterion['gift_selection_count']
  display_name: CouponCriterion['display_name']
  created_at: CouponCriterion['created_at']
  updated_at: CouponCriterion['updated_at']
  giveaways: CouponCriterionGiveawayItem[]
  /**
   * 累贈次數，是 calculate API 計算出的結果，本身不屬於任何資料欄位
   * 只有已套用的 criterion 才會有 count 值
   */
  count?: Nullable<number>
  isGiveawayOutOfStock?: boolean
}

export interface AvailableCriterionGroup {
  /**
   * criterion group id
   */
  id: number
  /**
   * coupon id
   */
  coupon_id: Coupon['id']
  criterions: AvailableCouponCriterion[]
  /**
   * 優惠類型
   *
   * 因為後台 「門檻設定」可以享優惠的區間只有一個，另一個 criterion_group 的 promotion_type 會是 null
   */
  promotion_type: PromotionType | null
  /**
   * 優惠券類型
   */
  type: CouponType
  /**
   * 此優惠券適用的運送類別
   */
  product_delivery_classes: Nullable<DeliveryClass[]>
  applicable_product_scope: ApplicableProductScope
  discount_trigger_method: DiscountTriggerMethod
}

/**
 * key 和 product_id 是商品 variation_id
 */
export type AvailableProductCoupons = Record<number, {
  product_id: number
  coupon_ids: AvailableCoupon['coupon_id'][]
}>

/**
 * 可參考後端 AvailableCouponResource.php
 */
export interface AvailableCoupon {
  id: number
  coupon_id: Coupon['id']
  criterion_groups: AvailableCriterionGroup[]
  display_name: string
  start_time: Nullable<string>
  end_time: Nullable<string>
  /**
   * 已達到全體使用上限
   */
  is_over_ticket_limit: boolean
  /**
   * 是否顯示在全站進度條
   */
  is_progress_visible: boolean
  /**
   * 已達到個人使用上限
   */
  is_over_user_limit: boolean
  /**
   * 是否為常態滿額禮
   */
  is_regular_giveaway: boolean
  /**
   * 是否可重複觸發
   */
  is_repeatable: boolean
  /**
   * 新客或舊客限制
   */
  new_or_existing_customer_restriction: NewOrExistingCustomerRestriction
  /**
   * 使用方式
   */
  usage_method: UsageMethod
  /**
   * 已使用次數
   */
  usage_count: Nullable<number>
  /**
   * 使用次數限制
   */
  usage_limit: Nullable<number>
  /**
   * 優惠券代碼
   */
  coupon_code: Nullable<string>
  /**
   * 縮圖
   */
  thumbnail_url: Nullable<string>
  /**
   * 信用卡驗證，判斷卡種
   */
  credit_card_affiliate_code: string
  promotion_group_id: Nullable<number>
  issuance: TicketIssuance
  /** 已領取會員票券數量 */
  ticket_claimed_count: Nullable<number>
}

export interface CreateEditCouponAppliedProduct {
  project_code: ProjectCode
  products: {
    product_id: CouponProduct['product_id']
    sku: CouponProduct['sku']
    variation_products: {
      variation_id: number
      sku: string
    }[]
  }[]
}

export interface CouponCriterionGroupBase {
  promotion_type: PromotionType
  discount_trigger_method: DiscountTriggerMethod
  applicable_product_scope: ApplicableProductScope
  product_delivery_classes: PRODUCT_DELIVERY_CLASSES[]
  applied_products: Nullable<AppliedProduct[]>
  excluded_products: Nullable<AppliedProduct[]>
  applied_shipping_methods: Nullable<string[]>
  is_multi_stage: boolean
  is_repeatable: boolean
  is_check_top_down: boolean
  applied_product_categories: Nullable<{
    project_code: ProjectCode
    product_categories: string[]
  }[]>
  discount_application_method: DiscountApplicationMethod
  criterions: CouponCriterionBase[]
  addon_purchase_limit: Nullable<number>
}

export interface CouponCriterionGroup extends CouponCriterionGroupBase {
  id: number
  coupon_id: number
  criterions: CouponCriterion[]
}
/**
 * SPEC: 計算方式
 * https://www.notion.so/shepherdtech/B1-Banner-Footer-136a4b7afc8880eeb2f8f34853746617?pvs=4#15c6ab697745453cb803c01fbbe490a1
 */
export interface CartCalculateResult {
  cart_uuid: string
  /**
   * 商品總額
   */
  subtotal: number
  /**
   * 折後金額
   * 1. 計算免運用的金額
   * 2. 計算滿額贈品的金額
   */
  total_after_discount: number
  /**
   * 運費原始金額
   */
  shipping_subtotal: number
  /**
   * 運費折後金額
   */
  shipping_total: number
  /**
   * 商品小計
   */
  total_after_shipping: number
  /**
   * 訂單總額
   */
  total: number
  /**
   * 稅金，total 是已經包含稅金的金額，這邊只顯示給顧客用
   */
  tax?: number
  /**
   * 稅前總金額
   */
  total_before_tax?: number
  /** 排除 addon_v2 價格 */
  total_without_addon_v2?: number
  applied_shipping_method_id: number
  applied_payment_method_id: number
  /**
   * 訂單商品套用的優惠
   */
  order_items: {
    sku: Nullable<FastpassOrderItem['sku']>
    sale_price: Nullable<FastpassOrderItem['sale_price']>
    applied_coupon_ids: Nullable<FastpassOrderItem['applied_coupon_ids']>
    delivery_class: Nullable<DeliveryClass>
    subship_info?: SubshipInfo
    id: number
    quantity: number
    is_addon_v2: boolean
    /** 後端已經將數量呈上特價金額 */
    addon_v2_price: Nullable<number>
    parent_product_id: Nullable<number>
    /** 單品加價購主商品 id */
    addon_main_product_id: Nullable<number>
  }[]
  /**
   * 顯示訂單明細用的
   */
  discount_items: DiscountItem[]
  /**
   * 訂單套用的優惠券
   */
  applied_coupons: AppliedCoupon[]
  /**
   * 目前可用得運費方法
   */
  shipping_methods: ShippingMethod[]
  /**
   * 目前可用得付款方式
   */
  payment_methods: PaymentMethod[]
  /**
   * 僅業務專站使用
   * 阻擋購物車不滿 4000 元
   */
  notice_info?: NoticeInfo[] | null
  /**
   * 購物車驗證訊息，例如：'code: 12001' 表示限購商品
   */
  cart_validation_messages: CartValidationMessages[]
  giveaways: Giveaways[]
  /** 加價購總金額 */
  addon_v2_total: number
  /** 加價購商品折扣前金額合計 */
  addon_v2_total_before_discount: number
}

export interface NoticeInfo {
  type: string
  message: string
  id: number
}

export interface DiscountItem {
  type: DiscountItemType
  display: string
  discount_amount: number
  is_cart_level: boolean
  coupon_id: Coupon['id'] | null
}

export interface AppliedCouponCriterion {
  coupon_id: CouponCriterion['coupon_id']
  id: CouponCriterion['id']
  criterion_quantity: CouponCriterion['criterion_quantity']
  criterion_amount: CouponCriterion['criterion_amount']
  applied_status: Nullable<AppliedStatus>
  left_quantity: Nullable<number>
  left_amount: Nullable<number>
  count?: Nullable<number>
  is_repeatable?: boolean
}

export interface AppliedCoupon {
  coupon_id: Coupon['id']
  criterions: AppliedCouponCriterion[]
}

export interface ProductOption {
  product_id: WpProduct['product_id']
  display_name: string
}

export interface SelectedCouponGiveawayProduct {
  project_code: ECommerceProduct['project_code']
  product_id: ECommerceProduct['id']
  sku: ECommerceProduct['sku']
  quantity: number
  isReadyOnly?: boolean
}

export interface SelectedCouponGiveaway {
  coupon_criterion_id: CouponCriterion['id']
  products: SelectedCouponGiveawayProduct[]
  /**
   * 是否為只讀狀態
   * 1. 如果贈品是全贈，則預選 -> isReadyOnly = true
   * 2. 如果贈品是單選，則預選 -> isReadyOnly = true
   * 3. 如果贈品是多選，則自選 -> isReadyOnly = false
   */
  isReadyOnly?: boolean
}

export interface Giveaways {
  coupon_criterion_id: number
  display_name: string
  product_options: ProductOption
}

/**
 * 錯誤代碼文件: https://docs.google.com/spreadsheets/d/1k-G08qpwo6TbGsrIWTGuuZHmiFXuy54O69YggI9tv3Q/edit?gid=575253350#gid=575253350
 */
export const CouponExceptionCode = {
  COUPON_NO_TICKET: 10001,
  COUPON_REACH_USER_LIMIT: 10002,
  COUPON_EXPIRED: 10003,
  COUPON_NO_PERMISSION: 10004,
  COUPON_CODE_NOT_EXIST: 10005,
  MANUAL_COUPONS_FAILED: 10006,
  WRONG_GIFT_SELECTION: 10007,
  KOL_COUPON_EXCEED: 10008,
  USER_RESTRICTION: 10009,
  INVALID_CRITERION_SELECTION: 10011,
  LOSE_CRITERION_SELECTION: 10012,
  COUPON_NOT_STARTED: 10022,
} as const

export type CouponExceptionCodeType = typeof CouponExceptionCode[keyof typeof CouponExceptionCode]

export const ReferralCouponExceptionCode = {
  REFERRAL_USE_ONE_COUPON: 11002,
  REFERRAL_INVALID_PLATFORM: 11003,
  REFERRAL_INVALID_FORMAT: 11004,
  NOT_OVER_MINIMUM_CART_THRESHOLD: 11005,
  OLD_CUSTOMER_USE_REFERRAL_COUPON: 11006,
  OWNER_USE_REFERRAL_COUPON: 11007,
  REFERRAL_COUPON_BIND_STATUS_INVALID: 11008,
} as const

export type ReferralCouponExceptionCodeType = typeof ReferralCouponExceptionCode[keyof typeof ReferralCouponExceptionCode]

export const StockExceptionCode = {
  OUT_OF_STOCK: 9003,
} as const

export type StockExceptionCodeType = typeof StockExceptionCode[keyof typeof StockExceptionCode]

export const LifetimeLimitedProductExceptionCode = {
  LIFETIME_LIMITED_EXCEED: 12001,
} as const

export type LifetimeLimitedExceptionCodeType = typeof LifetimeLimitedProductExceptionCode[keyof typeof LifetimeLimitedProductExceptionCode]

export const PaymentTransactionExceptionCode = {
  NONCANCELLABLE_PAYMENT_TRANSACTION: 16001,
  BLACKLIST_PHONE_NUMBER: 16002,
  CART_NOT_FOUND: 16003,
  /** 帳單郵遞區號格式錯誤（需為純數字） */
  INVALID_BILLING_POSTCODE: 16013,
  MULTI_ACCOUNT_FIRST_PURCHASE_ABUSE: 6000,
} as const

export type PaymentTransactionExceptionCodeType = typeof PaymentTransactionExceptionCode[keyof typeof PaymentTransactionExceptionCode]

export type PrivateProductExceptionCodeType = typeof PRIVATE_PRODUCT_ERROR_CODES[keyof typeof PRIVATE_PRODUCT_ERROR_CODES]

export interface CartValidationDetail {
  message: string
  product_id?: number
  product_name?: string
  cart_threshold?: number
  referral_coupon_code?: string
  first_coupon_code?: string
  second_coupon_code?: string
  coupon_id?: number
  coupon_code?: string
  coupon_name?: string
  sku?: string
  left_quantity?: number
  limit?: number
  actual_quantity?: number
  parent_product_id?: number
  addon_main_product_id?: number
  violations?: {
    addon_setting_id: number
    limit: number
    parent_product_id: number
    parent_product_limit: number
    product_name: string
    quantity: number
    sku: string
  }[]
  addon_setting_id?: number
}

export interface CartValidationMessages {
  code: number
  message: CartValidationDetail
}

export interface guestDiscountCalculateResult {
  discount_total: number
  discount_items: DiscountItem[]
  applied_coupons: AppliedCoupon[]
  giveaways: Giveaways[]
  order_items: {
    sku: Nullable<FastpassOrderItem['sku']>
    sale_price: Nullable<FastpassOrderItem['sale_price']>
    applied_coupon_ids: Nullable<FastpassOrderItem['applied_coupon_ids']>
    delivery_class: Nullable<DeliveryClass>
    subship_info?: SubshipInfo
    id: number
    quantity: number
    is_addon_v2: boolean
    /** 後端已經將數量呈上特價金額 */
    addon_v2_price: Nullable<number>
    parent_product_id: Nullable<number>
    addon_main_product_id: Nullable<number>
  }[]
}

export const SEND_JOB_TYPE = {
  ONCE: 'once',
  RECURRING: 'recurring',
} as const

export const SEND_JOB_STATUS = {
  NOT_ENABLED: 'not_enabled',
  SCHEDULED: 'scheduled',
  COMPLETED: 'completed',
  EXPIRED: 'expired',
} as const

export const RECURRING_STATUS = {
  NOT_ENABLED: 'not_enabled',
  ENABLED: 'enabled',
  PAUSED: 'paused',
} as const

export const AUDIENCE_SOURCE = {
  MANUAL: 'manual',
  SEGMENT: 'segment',
} as const

export interface CouponSendJobMemberSegment {
  id: number
  name: string
}

export interface CouponSendJobUser {
  wp_user_id: number
  display_name: string
  is_sent: boolean
  sent_at: Nullable<string>
  error_message: Nullable<string>
}

export interface CouponSendJob {
  id: number
  coupon_id: number
  type: ValueOf<typeof SEND_JOB_TYPE>
  audience_source: ValueOf<typeof AUDIENCE_SOURCE>
  member_segment: CouponSendJobMemberSegment | null
  send_time: Nullable<string>
  expected_send_time: Nullable<string>
  is_enabled: boolean
  status: ValueOf<typeof SEND_JOB_STATUS>
  recurring_status: ValueOf<typeof RECURRING_STATUS>
  executed_at: Nullable<string>
  users: CouponSendJobUser[]
  sent_count: number
  failed_count: number
  total_user_count: number
  created_at: string
  updated_at: string
}
